import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  User, Building2, Phone, Mail, Zap, LogOut, Save, Loader2, CheckCircle, BadgeCheck,
  CalendarDays, Check, Copy, ExternalLink, HelpCircle, RefreshCw,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { usersApi, practiceApi, errorDetailText } from '../services/api';
import { CalendarToken } from '../types';
import { isTwa } from '../utils/platform';
import { tierMeta } from '../lib/meta';
import { PhoneVerifyModal } from '../components/ui';
import { TourReplayList } from '../tours/TourReplayList';

export function SettingsPage() {
  const { user, logout, updateUser } = useAuthStore();
  const { hash } = useLocation();

  // Deep links like /app/settings#reminders (Today's calendar shortcut) —
  // react-router doesn't scroll to hashes on client-side navigation.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash]);

  const [fullName, setFullName] = useState(user?.full_name || '');
  const [organization, setOrganization] = useState(user?.organization || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [showVerify, setShowVerify] = useState(false);
  const phoneVerified = !!user?.phone_verified;

  // Practice: email digests toggle + court-calendar subscription link.
  const [isTogglingReminders, setIsTogglingReminders] = useState(false);
  const [practiceError, setPracticeError] = useState('');
  const [calendar, setCalendar] = useState<CalendarToken | null>(null);
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [copied, setCopied] = useState(false);

  const toggleReminders = async () => {
    if (isTogglingReminders) return;
    setIsTogglingReminders(true);
    setPracticeError('');
    try {
      const { data } = await usersApi.updateProfile({
        practice_email_reminders: !user?.practice_email_reminders,
      });
      updateUser(data);
    } catch (err: any) {
      setPracticeError(errorDetailText(err, 'Could not update the reminder setting.'));
    } finally {
      setIsTogglingReminders(false);
    }
  };

  const showCalendarLink = async () => {
    if (isLoadingCalendar) return;
    setIsLoadingCalendar(true);
    setPracticeError('');
    try {
      const { data } = await practiceApi.getCalendarToken();
      setCalendar(data);
    } catch (err: any) {
      setPracticeError(errorDetailText(err, 'Could not load the calendar link.'));
    } finally {
      setIsLoadingCalendar(false);
    }
  };

  const rotateCalendarLink = async () => {
    if (isRotating) return;
    if (!confirm('Rotate the calendar link? The old link stops working.')) return;
    setIsRotating(true);
    setPracticeError('');
    try {
      const { data } = await practiceApi.rotateCalendarToken();
      setCalendar(data);
    } catch (err: any) {
      setPracticeError(errorDetailText(err, 'Could not rotate the calendar link.'));
    } finally {
      setIsRotating(false);
    }
  };

  const copyIcsUrl = async () => {
    if (!calendar) return;
    try {
      await navigator.clipboard.writeText(calendar.ics_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the URL is visible to copy manually */
    }
  };

  const tier = user?.subscription_tier || 'free';
  const isFreeTier = tier === 'free';
  const usedPct = user
    ? Math.min(100, Math.round((user.monthly_jobs_used / user.monthly_jobs_limit) * 100))
    : 0;
  const draftsPct =
    user && typeof user.monthly_drafts_limit === 'number' && user.monthly_drafts_limit > 0
      ? Math.min(100, Math.round(((user.monthly_drafts_used ?? 0) / user.monthly_drafts_limit) * 100))
      : 0;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);
    try {
      const { data } = await usersApi.updateProfile({ full_name: fullName, organization, phone });
      updateUser(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-legal-900">Account Settings</h1>
        <p className="text-legal-500 text-sm mt-1">Manage your profile and subscription</p>
      </div>

      {/* Profile */}
      <section className="bg-white border border-legal-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-legal-100 flex items-center space-x-2">
          <User className="h-4 w-4 text-legal-500" />
          <h2 className="font-semibold text-legal-800">Profile</h2>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Email — read only */}
          <div>
            <label className="block text-sm font-medium text-legal-700 mb-1.5">
              <span className="flex items-center space-x-1.5">
                <Mail className="h-3.5 w-3.5" />
                <span>Email</span>
              </span>
            </label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-3 py-2 border border-legal-200 rounded-lg bg-legal-50 text-legal-500 text-sm cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-legal-400">Email cannot be changed</p>
          </div>

          {/* Full name */}
          <div>
            <label className="block text-sm font-medium text-legal-700 mb-1.5">Full Name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              minLength={2}
              className="w-full px-3 py-2 border border-legal-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
            />
          </div>

          {/* Organization */}
          <div>
            <label className="block text-sm font-medium text-legal-700 mb-1.5">
              <span className="flex items-center space-x-1.5">
                <Building2 className="h-3.5 w-3.5" />
                <span>Organisation / Chamber</span>
              </span>
            </label>
            <input
              type="text"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              placeholder="e.g. Advocate, High Court of Madras"
              className="w-full px-3 py-2 border border-legal-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-legal-700 mb-1.5">
              <span className="flex items-center space-x-1.5">
                <Phone className="h-3.5 w-3.5" />
                <span>Phone</span>
                {phoneVerified && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                    <BadgeCheck className="h-3.5 w-3.5" /> Verified
                  </span>
                )}
              </span>
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210"
              readOnly={phoneVerified}
              className={`w-full px-3 py-2 border border-legal-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent ${
                phoneVerified ? 'bg-legal-50 text-legal-500 cursor-not-allowed' : ''
              }`}
            />
            {phoneVerified ? (
              <p className="text-xs text-legal-400 mt-1.5">
                Verified via WhatsApp — contact support to change it.
              </p>
            ) : (
              <button
                type="button"
                onClick={() => setShowVerify(true)}
                className="mt-1.5 text-xs font-medium text-emerald-700 hover:text-emerald-800 transition-colors"
              >
                Verify via WhatsApp →
              </button>
            )}
          </div>

          <div className="flex items-center justify-between pt-2">
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center space-x-2 px-5 py-2 bg-accent text-legal-900 font-semibold rounded-lg hover:bg-accent-400 transition-colors disabled:opacity-60 text-sm"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : saved ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>{saved ? 'Saved!' : 'Save Changes'}</span>
            </button>
          </div>
        </form>
      </section>

      {/* Plan & usage — every meter in one place, in words (spec §4.7) */}
      <section className="bg-white border border-legal-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-legal-100 flex items-center space-x-2">
          <Zap className="h-4 w-4 text-legal-500" />
          <h2 className="font-semibold text-legal-800">Plan & usage</h2>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-legal-500">Current plan</p>
              <div className="flex items-center space-x-2 mt-1">
                <span className={`text-sm font-semibold px-2.5 py-0.5 rounded-full ${tierMeta(tier).chip}`}>
                  {tierMeta(tier).label}
                </span>
              </div>
            </div>
            {isFreeTier && !isTwa() && (
              <Link
                to="/pricing"
                data-track="settings-upgrade"
                className="inline-flex items-center space-x-1.5 px-4 py-2 bg-accent text-legal-900 font-semibold rounded-lg hover:bg-accent-400 transition-colors text-sm"
              >
                <Zap className="h-3.5 w-3.5" />
                <span>Upgrade</span>
              </Link>
            )}
          </div>

          {user && (
            <div className="space-y-4">
              {/* Researches */}
              <div>
                <div className="flex justify-between text-sm text-legal-600 mb-1.5">
                  <span>Researches this month</span>
                  <span className={usedPct >= 80 ? 'text-orange-500 font-medium' : ''}>
                    {user.monthly_jobs_used} of {user.monthly_jobs_limit} used
                  </span>
                </div>
                <div className="w-full bg-legal-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      usedPct >= 90 ? 'bg-red-400' : usedPct >= 70 ? 'bg-orange-400' : 'bg-accent'
                    }`}
                    style={{ width: `${usedPct}%` }}
                  />
                </div>
              </div>

              {/* Standard drafts */}
              <div>
                <div className="flex justify-between text-sm text-legal-600 mb-1.5">
                  <span>Standard drafts this month</span>
                  {typeof user.monthly_drafts_limit === 'number' ? (
                    <span className={draftsPct >= 80 ? 'text-orange-500 font-medium' : ''}>
                      {user.monthly_drafts_used ?? 0} of {user.monthly_drafts_limit} used
                    </span>
                  ) : (
                    <span className="text-emerald-600 font-medium">Unlimited on your plan</span>
                  )}
                </div>
                {typeof user.monthly_drafts_limit === 'number' && (
                  <div className="w-full bg-legal-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        draftsPct >= 90 ? 'bg-red-400' : draftsPct >= 70 ? 'bg-orange-400' : 'bg-accent'
                      }`}
                      style={{ width: `${draftsPct}%` }}
                    />
                  </div>
                )}
              </div>

              <p className="text-xs text-legal-400">
                Drafts from a research memo: 2 included with every memo — they don't count
                against standard drafts.
              </p>

              {isFreeTier && (
                <p className="text-xs text-legal-400">
                  Free plan includes 3 researches and 3 standard drafts a month.{' '}
                  {isTwa() ? (
                    <span>Upgrade at urava.app.</span>
                  ) : (
                    <Link to="/pricing" data-track="settings-see-plans" className="text-accent hover:underline">
                      See all plans →
                    </Link>
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Hearing reminders & calendar (spec §4.7 — was "Practice") */}
      <section id="reminders" className="bg-white border border-legal-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-legal-100 flex items-center space-x-2">
          <CalendarDays className="h-4 w-4 text-legal-500" />
          <h2 className="font-semibold text-legal-800">Hearing reminders & calendar</h2>
        </div>

        <div className="p-6 space-y-6">
          {practiceError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {practiceError}
            </div>
          )}

          {/* Email digests */}
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!user?.practice_email_reminders}
              onChange={toggleReminders}
              disabled={isTogglingReminders}
              className="mt-0.5 h-4 w-4 rounded border-legal-300 text-accent focus:ring-accent"
            />
            <span className="text-sm text-legal-700">
              <span className="font-medium text-legal-800 inline-flex items-center gap-1.5">
                Evening & morning hearing digests by email
                {isTogglingReminders && <Loader2 className="h-3.5 w-3.5 animate-spin text-legal-400" />}
              </span>
              <span className="block text-xs text-legal-400 mt-0.5">
                Tomorrow's list the evening before, today's list at daybreak.
              </span>
            </span>
          </label>

          {/* Court calendar */}
          <div>
            <p className="flex items-center gap-1.5 text-sm font-medium text-legal-800 mb-1">
              <CalendarDays className="h-3.5 w-3.5 text-legal-500" /> Court calendar
            </p>
            <p className="text-xs text-legal-400 mb-3">
              Subscribe from Google Calendar or your phone's calendar app — every hearing appears automatically.
            </p>
            {!calendar ? (
              <button
                type="button"
                onClick={showCalendarLink}
                disabled={isLoadingCalendar}
                className="inline-flex items-center space-x-2 px-4 py-2 border border-legal-300 text-legal-700 rounded-lg hover:bg-legal-50 transition-colors text-sm font-medium disabled:opacity-60"
              >
                {isLoadingCalendar ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CalendarDays className="h-4 w-4" />
                )}
                <span>Show subscription link</span>
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={calendar.ics_url}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 px-3 py-2 border border-legal-200 rounded-lg bg-legal-50 text-legal-600 text-xs font-mono"
                  />
                  <button
                    type="button"
                    onClick={copyIcsUrl}
                    aria-label="Copy calendar URL"
                    className="p-2 border border-legal-300 rounded-lg text-legal-500 hover:text-legal-800 hover:bg-legal-50 transition-colors shrink-0"
                  >
                    {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(calendar.webcal_url)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1.5 px-4 py-2 bg-accent text-legal-900 font-semibold rounded-lg hover:bg-accent-400 transition-colors text-sm"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span>Add to Google Calendar</span>
                  </a>
                  <a
                    href={calendar.webcal_url}
                    className="inline-flex items-center space-x-1.5 px-4 py-2 border border-legal-300 text-legal-700 rounded-lg hover:bg-legal-50 transition-colors text-sm font-medium"
                  >
                    <CalendarDays className="h-3.5 w-3.5" />
                    <span>Open webcal link</span>
                  </a>
                  <button
                    type="button"
                    onClick={rotateCalendarLink}
                    disabled={isRotating}
                    className="inline-flex items-center space-x-1.5 px-4 py-2 border border-legal-300 text-legal-500 rounded-lg hover:text-legal-800 hover:bg-legal-50 transition-colors text-sm disabled:opacity-60"
                  >
                    {isRotating ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" />
                    )}
                    <span>Rotate link</span>
                  </button>
                </div>
                <p className="text-xs text-legal-400">
                  Read-only. Google refreshes subscribed calendars about once a day — the board is always current.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Help */}
      <section className="bg-white border border-legal-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-legal-100 flex items-center space-x-2">
          <HelpCircle className="h-4 w-4 text-legal-500" />
          <h2 className="font-semibold text-legal-800">Help</h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-legal-800">How Urava works</p>
              <p className="text-xs text-legal-400 mt-0.5">
                From question to court-ready memo — a short guide to every part of the app.
              </p>
            </div>
            <Link
              to="/user-guide"
              data-track="settings-help-guide"
              className="inline-flex items-center space-x-1.5 px-4 py-2 border border-legal-300 text-legal-700 rounded-lg hover:bg-legal-50 transition-colors text-sm font-medium shrink-0"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              <span>Open the guide</span>
            </Link>
          </div>
          {/* Replayable guided tours — the permanent retrieval point after
              dismissal (tour spec §2.3, §5). */}
          <div className="pt-4 border-t border-legal-100">
            <p className="text-sm font-medium text-legal-800 mb-3">Show me around</p>
            <TourReplayList />
          </div>
        </div>
      </section>

      {/* Danger zone */}
      <section className="bg-white border border-legal-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-legal-100">
          <h2 className="font-semibold text-legal-800">Session</h2>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-legal-800">Sign out</p>
              <p className="text-xs text-legal-400 mt-0.5">You'll need to sign in again to access your account</p>
            </div>
            <button
              onClick={logout}
              className="inline-flex items-center space-x-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </section>

      <PhoneVerifyModal
        open={showVerify}
        onClose={() => setShowVerify(false)}
        onVerified={() => {
          setShowVerify(false);
          const verified = useAuthStore.getState().user?.phone;
          if (verified) setPhone(verified);
        }}
      />
    </div>
  );
}
